import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Device from 'device/device';
import _get from 'lodash/get';
import _set from 'lodash/set';
import {
    PlayerControls as MLBPlayerControls
} from 'mlb-player-controls-react';
import MediaPlayer from '../../components/media-player/media-player';
import LeaveBehind from '../../components/leave-behind/leave-behind';
import MediaItem from '../../models/media-item';
import Playlist from '../../models/playlist';
import PluginConfigHelper from '../../utils/config/plugin-config-helper';
import './app.scss';

class App extends Component {

    constructor(props) {

        super(props);

        const {
            initialMedia,
            list,
            requestParams: {
                query,
            },
        } = this.props;

        this.mediaConfig = this.setUpMediaConfig();
        const clip = new MediaItem(_get(initialMedia, 'metaData', {}), this.mediaConfig, requestParams);
        this.playlist = new Playlist(list, clip.contentId, this.mediaConfig);
        this.mediaPlayer = React.createRef();

        this.showLeaveBehind = this.showLeaveBehind.bind(this);
        this.onSelectItem = this.onSelectItem.bind(this);

        this.lbType = _get(query, 'lb');
        this.leaveBehindEnabled = _get(query, 'leaveBehindEnabled', false);
        this.autoPlayParam = _get(query, 'autoPlay') || _get(query, 'autoplay', false);
        this.autoPlay = [
            true,
            'true',
            1,
            '1',
        ].indexOf(this.autoPlayParam) > -1 ? true : false;

        // If autoplay is enabled, then force mute
        if(this.autoPlay) {

            this.mute = true;

        }

        this.state = {
            leaveBehind: false,
            mediaPlayer: undefined,
            clip,
        };

        this.onMediaPlayerReady = this.onMediaPlayerReady.bind(this);

        this.deviceInfo = Device();
        this.isMobile = [
            'phone',
            'tablet',
        ].indexOf(this.deviceInfo.formFactor) > -1 ? true : false;

    }

    componentWillUpdate(nextProps, nextState) {

        if (this.state.leaveBehind !== nextState.leaveBehind
            && !nextState.leaveBehind
            && nextState.mediaPlayer) {

            const {
                mediaPlayer,
            } = nextState;

            mediaPlayer.observationCenter.controlsSubject.next({
                type: 'SHOW_CONTROLS',
            });

        }

    }

    setUpMediaConfig() {

        const mediaConfig = this.props.mediaConfig;
        const pluginConfigs = mediaConfig.pluginConfigs || [];

        // Get Communication Plugin Configs
        pluginConfigs.push(PluginConfigHelper.getEventReplicatorConfig(mediaConfig));
        pluginConfigs.push(PluginConfigHelper.getBandwagonConfig(mediaConfig));

        // Get Advertising Plugin Configs
        pluginConfigs.push(PluginConfigHelper.getAdvertisingPluginConfig(mediaConfig));

        // Get Analytics Plugin Configs
        pluginConfigs.push(PluginConfigHelper.getOmniturePluginConfig(mediaConfig));
        pluginConfigs.push(PluginConfigHelper.getChartBeatPluginConfig(mediaConfig));
        pluginConfigs.push(PluginConfigHelper.getConvivaPluginConfig(mediaConfig));

        _set(mediaConfig, 'pluginConfigs', pluginConfigs);

        return mediaConfig;

    }

    showLeaveBehind() {

        this.setState({ leaveBehind: true });

    }

    onSelectItem(clip) {

        if (clip.contentId === this.state.clip.contentId) {

            const mediaPlayer = _get(this, 'mediaPlayer.current', { replay: () => null });

            this.setState({
                leaveBehind: false,
            });

            mediaPlayer.replay();

            return;

        }

        clip.getMetadata(this.mediaConfig)
            .then(() => {

                this.playlist.setSelectedItem(clip.contentId);

                this.setState({
                    clip,
                    leaveBehind: false,
                });

            })
            .catch((error) => {

                console.error('There was an error retrieving item metadata', error);

            });

    }

    maybeRenderLeaveBehind() {

        const {
            leaveBehind,
            mediaPlayer,
        } = this.state;

        const showLeaveBehind = leaveBehind && this.leaveBehindEnabled;

        if (showLeaveBehind) {

            mediaPlayer.observationCenter.controlsSubject.next({
                type: 'HIDE_CONTROLS',
            });

            return <LeaveBehind onSelectItem={this.onSelectItem} playlist={this.playlist} type={this.lbType} />;

        }

    }

    onMediaPlayerReady(mediaPlayer) {

        this.setState({ mediaPlayer });

    }

    render() {

        const { clip } = this.state;
        const {
            fullscreenElementId,
            webMediaApm,
        } = this.props;

        return (
            <div className="app">
                <MediaPlayer
                    ref={this.mediaPlayer}
                    onComplete={this.showLeaveBehind}
                    clip={clip}
                    autoplay={this.autoPlay}
                    mute={this.mute}
                    mediaConfig={this.mediaConfig}
                    mediaPlayerReady={this.onMediaPlayerReady}
                    deviceInfo={this.deviceInfo}
                    webMediaApm={webMediaApm}
                />
                {this.state.mediaPlayer !== undefined
                    && !this.isMobile
                    ? <MLBPlayerControls
                        mediaPlayer={this.state.mediaPlayer}
                        playerWrapper={document.querySelector('#media-player')}
                        fullscreenElementId={fullscreenElementId}
                    />
                    : null
                }
                {this.maybeRenderLeaveBehind()}
            </div>
        );

    }

}

App.propTypes = {
    /** The media configuration object */
    mediaConfig: PropTypes.shape({ embed: PropTypes.object }),
    /** An object containing the request params such as autoPlay, mute etc. */
    requestParams: PropTypes.shape({
        query: PropTypes.object,
    }),
    /** The media playlist. A list of media items */
    list: PropTypes.arrayOf(PropTypes.object),
    /** The id of the fullscreen target element. Defaults to "media-player" */
    fullscreenElementId: PropTypes.string,
    webMediaApm: PropTypes.shape({
        appName: PropTypes.string,
        serviceUrl: PropTypes.string,
        getCurrentTransaction: PropTypes.func,
        startTransaction: PropTypes.func,
        startSpan: PropTypes.func,
    }),
};

App.defaultProps = {
    mediaConfig: {},
    requestParams: {
        query: {},
    },
    list: [],
    fullscreenElementId: 'media-player',
    webMediaApm: {
        _apm: {
            appName: '',
            serviceUrl: '',
            getCurrentTransaction: () => {},
            startTransaction: () => {},
            startSpan: () => {},
        },
    },
};

export default App;
